import { useCallback, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ERROR_CODES } from '@icebreaker/shared';
import { Button, Skeleton } from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { subSurfacePermission } from '@/app/screen-permissions';
import { PermissionSurfaceGate } from '@/features/access/ui/permission-gate';
import adminStyles from '@/features/admin/ui/admin.module.css';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { fetchOrganization } from '../store/organizations.slice';
import { ContractCard } from './contract-card';
import { GRANT_ON_OPEN_PARAM } from './create-organization-dialog';
import { CreditLedgerCard } from './credit-ledger-card';
import { CreditSummaryCard } from './credit-summary-card';
import { OrganizationCoverCard } from './organization-cover-card';
import { OrganizationIdentityCard } from './organization-identity-card';
import { OrganizationOwnerCard } from './organization-owner-card';
import { OrganizationPostEventSection } from './organization-post-event-section';
import { OrganizationReportsSection } from './organization-reports-section';
import styles from './organizations.module.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * KURUM DETAYI (P5) — beş bölüm: künye, sahip, kredi özeti, sözleşme, defter.
 * Kurum kapağı turunda (Story P1) sahibin altına kapak görseli kartı eklendi.
 *
 * BÖLÜMLER BAĞIMSIZ YÜKLENİR (referans): künye + sahip kurum ucundan, özet +
 * sözleşme kredi özetinden, defter defter ucundan gelir; biri hata verirse
 * yalnız o kartta "Yeniden dene" çıkar, diğerleri çalışır.
 *
 * KREDİ BÖLÜMLERİ AYRI YETKİDİR (`staff:credit_manage`): kurum ekranını
 * görebilen ama kredi yetkisi olmayan personelde kredi kartları çizilmez.
 *
 * ETKİNLİK RAPORLARI (organizatör raporu Story 5) en altta, kendi kapısıyla
 * (`staff:organization_provision`): yetkisiz personelde bölüm DOM'a hiç girmez.
 *
 * ETKİNLİK SONRASI (belge ve anket Story 8) raporların altında, aynı desenle kendi
 * kapısıyla; içindeki "Anketi kapat" ayrıca `staff:moderate` ister.
 */
export function OrganizationDetailScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const { organizationId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const detail = useAppSelector((root: RootState) => root.panelOrgs.detail);
  const valid = UUID.test(organizationId);
  const openGrant = params.get(GRANT_ON_OPEN_PARAM) === '1';

  const load = useCallback(() => {
    if (valid) dispatch(fetchOrganization.request({ id: organizationId }));
  }, [dispatch, organizationId, valid]);

  useEffect(() => {
    load();
  }, [load]);

  const clearGrantParam = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete(GRANT_ON_OPEN_PARAM);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const org = detail.id === organizationId ? detail.data : null;
  const notFound =
    !valid ||
    (detail.phase === 'failed' &&
      (detail.error?.code === ERROR_CODES.ORGANIZATION_NOT_FOUND ||
        detail.error?.statusCode === 404));

  const back = (
    <Link className={styles.backLink} to={ROUTES.organizations}>
      <ArrowLeft size={16} aria-hidden="true" />
      {t.t('admin.orgs.detail.back')}
    </Link>
  );

  if (notFound) {
    return (
      <section className={adminStyles.screen}>
        {back}
        <div className={[styles.card, styles.sectionState].join(' ')} role="status">
          <p className={styles.contractNone}>{t.t('admin.orgs.detail.notFound')}</p>
          <Link className={styles.backLink} to={ROUTES.organizations}>
            {t.t('admin.orgs.detail.backToList')}
          </Link>
        </div>
      </section>
    );
  }

  const name = org?.name ?? '';

  return (
    <section className={adminStyles.screen}>
      {back}
      <div className={styles.detailArea}>
        <div className={styles.detailGrid}>
          <div className={styles.leftColumn}>
            {org ? (
              <>
                <OrganizationIdentityCard org={org} />
                <OrganizationOwnerCard org={org} />
                <OrganizationCoverCard org={org} />
              </>
            ) : detail.phase === 'failed' ? (
              <div className={[styles.card, styles.sectionState].join(' ')} role="alert">
                <p className={styles.sectionError}>{t.t('admin.orgs.detail.loadError')}</p>
                <Button variant="secondary" size="sm" onClick={load}>
                  {t.t('admin.orgs.retry')}
                </Button>
              </div>
            ) : (
              <div className={styles.card}>
                <Skeleton label={t.t('admin.orgs.detail.loading')} lines={5} />
              </div>
            )}
            <PermissionSurfaceGate permission={subSurfacePermission('panel:organization-credits')}>
              <ContractCard organizationId={organizationId} organizationName={name} />
            </PermissionSurfaceGate>
          </div>
          <PermissionSurfaceGate
            permission={subSurfacePermission('panel:organization-credits')}
            fallback={null}
          >
            <CreditSummaryCard
              organizationId={organizationId}
              organizationName={name}
              openGrantOnMount={openGrant}
              onGrantOpened={clearGrantParam}
            />
          </PermissionSurfaceGate>
        </div>
        <PermissionSurfaceGate
          permission={subSurfacePermission('panel:organization-credits')}
          fallback={null}
        >
          <CreditLedgerCard organizationId={organizationId} />
        </PermissionSurfaceGate>
        <PermissionSurfaceGate
          permission={subSurfacePermission('panel:organization-event-reports')}
          fallback={null}
        >
          <OrganizationReportsSection organizationId={organizationId} />
        </PermissionSurfaceGate>
        <PermissionSurfaceGate
          permission={subSurfacePermission('panel:organization-post-event')}
          fallback={null}
        >
          <OrganizationPostEventSection organizationId={organizationId} />
        </PermissionSurfaceGate>
      </div>
    </section>
  );
}
